<script setup lang="ts">
  import { ref } from 'vue'
  import { IconRestore } from '@gat-ship/icons'
  import type { ActivateUserRequest, TenantUser } from '@gat-ship/types'
  import {
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection
  } from '@gat-ship/vue-components'
  import { globalUtils } from '@gat-ship/utils'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const currentTenantUser = ref<TenantUser | undefined>(undefined)

  async function activateUser() {
    const tenantUser = currentTenantUser.value
    if (!tenantUser) return
    isOpen.value = false

    function isValidRequestPayload(
      payload: Partial<ActivateUserRequest>
    ): payload is ActivateUserRequest {
      if (
        payload.userId &&
        payload.activeFrom &&
        globalUtils.date.isISODateTimeString(payload.activeFrom)
      ) {
        return true
      }
      return false
    }

    const payload = {
      userId: tenantUser.userId,
      activeFrom: globalUtils.date.dateNow()
    }
    if (isValidRequestPayload(payload)) {
      await tenantUserProvider.commands.activateUser(payload)
    }
  }

  export interface ShowActivateModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowActivateModalParams) {
    currentTenantUser.value = tenantUser
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <Modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconRestore class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Activate User
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="currentTenantUser">
        {{ currentTenantUser.fullName }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <p class="whitespace-pre-line text-sm leading-tight">
          This will give the user instant access to the application.
        </p>
        <p class="whitespace-pre-line text-sm leading-tight">
          Are you sure you want to activate this user?
        </p>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <Button variant="outline" @click="activateUser"> Activate User </Button>
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </Modal>
</template>
